<template>
    <div class="grid">
        <Toast/>

        <div class="col-12" style="max-width: 1000px;">
            <div class="card">
                <h4>Resumen de IVA por tarifa</h4>
                <p style="max-width: 720px;">
                    Insumo para llenar el <b>formulario 104</b> del período. Junta el IVA que generaste en
                    ventas, menos tus notas de crédito, y el crédito tributario de tus compras, desglosados
                    por tarifa tal como el formulario los pide: valor bruto, notas de crédito y valor neto.
                    <b>No sustituye a la declaración</b>: te evita cuadrar el detalle a mano.
                </p>

                <div style="display: flex; gap: 0.5rem; align-items: flex-end; flex-wrap: wrap;">
                    <div>
                        <label style="display: block; font-size: 0.85rem; color: #777;">Mes</label>
                        <Dropdown v-model="month" :options="months" optionLabel="label" optionValue="value"
                                  style="width: 150px;"/>
                    </div>
                    <div>
                        <label style="display: block; font-size: 0.85rem; color: #777;">Año</label>
                        <Dropdown v-model="year" :options="years" style="width: 110px;"/>
                    </div>
                    <Button label="Consultar" icon="pi pi-search" :loading="loading" @click="load"/>
                    <Button label="Excel" icon="pi pi-download" class="p-button-outlined" :disabled="!data"
                            :loading="downloading" @click="downloadExcel"/>
                </div>

                <div v-if="data">
                    <DataTable :value="rows" class="mt-4" :scrollable="true">
                        <Column field="concept" header="Concepto" style="min-width: 220px;">
                            <template #body="slotProps">
                                <span :style="slotProps.data.strong ? 'font-weight:700;' : ''">
                                    {{ slotProps.data.concept }}
                                </span>
                            </template>
                        </Column>
                        <!-- Las tres columnas de ventas del 104: bruto, notas de crédito y neto -->
                        <Column header="Ventas · valor bruto" style="min-width: 150px;">
                            <template #body="slotProps">
                                <span :style="slotProps.data.strong ? 'font-weight:700;' : ''">
                                    ${{ slotProps.data.sales }}
                                </span>
                            </template>
                        </Column>
                        <Column header="(−) Notas de crédito" style="min-width: 150px;">
                            <template #body="slotProps">
                                <span :style="slotProps.data.strong ? 'font-weight:700;' : ''">
                                    ${{ slotProps.data.notes }}
                                </span>
                            </template>
                        </Column>
                        <Column header="Ventas · valor neto" style="min-width: 150px;">
                            <template #body="slotProps">
                                <span :style="slotProps.data.strong ? 'font-weight:700;' : ''">
                                    ${{ slotProps.data.net }}
                                </span>
                            </template>
                        </Column>
                        <Column header="Compras (crédito tributario)" style="min-width: 200px;">
                            <template #body="slotProps">
                                <span :style="slotProps.data.strong ? 'font-weight:700;' : ''">
                                    ${{ slotProps.data.purchases }}
                                </span>
                            </template>
                        </Column>
                    </DataTable>

                    <Message :severity="data.result === 'toPay' ? 'warn' : 'success'" :closable="false" class="mt-3">
                        <b>{{ data.result === 'toPay' ? 'IVA a pagar' : 'Crédito tributario a favor' }}:</b>
                        ${{ money(data.amount) }}
                        <small style="display:block; color:#777;">
                            IVA generado neto ${{ money(data.netSales.iva) }} − crédito tributario ${{ money(data.purchases.iva) }}
                        </small>
                        <small v-if="data.creditNotes.count > 0" style="display:block; color:#777;">
                            IVA en ventas ${{ money(data.sales.iva) }} − notas de crédito ${{ money(data.creditNotes.iva) }}
                        </small>
                    </Message>

                    <!-- El 104 no admite un valor neto negativo: pasa con una N/C de una factura
                         de un mes anterior cuando el mes no tiene ventas que la absorban. -->
                    <Message v-if="carryLabels" severity="warn" :closable="false" class="mt-3">
                        Las notas de crédito superan las ventas en {{ carryLabels }}. El 104 no admite un valor
                        neto negativo: el excedente se declara como notas de crédito por compensar el próximo mes
                        (casillas 442/443 y 453).
                    </Message>

                    <!-- Anuladas en Contablus que el SRI sigue dando por vigentes: se declaran
                         (las facturas están dentro del IVA generado y las N/C se restan), pero
                         el usuario tiene que enterarse. -->
                    <div v-if="annulledUnreportedCount > 0 || notesAnnulledUnreportedCount > 0" class="card mt-3"
                         style="background:#fdecea; border-left:3px solid #d9534f;">
                        <h6 style="margin-top:0;">
                            <i class="pi pi-exclamation-triangle mr-2"></i>Anuladas en Contablus, vigentes en el SRI
                            <b v-if="annulledUnreportedCount > 0" style="float:right;">${{ money(data.annulledUnreported.iva) }}</b>
                        </h6>
                        <template v-if="annulledUnreportedCount > 0">
                            <p style="font-size: 0.9rem; color: #555; max-width: 640px;">
                                Anulaste estas facturas en Contablus, pero el SRI no confirmó la anulación: para el SRI
                                <b>siguen vigentes y están incluidas en el IVA generado</b>. Si la anulación es real,
                                usa «Reportar la anulación al SRI» desde la venta; el SRI la admite hasta el día 7 del
                                mes siguiente al de su emisión.
                            </p>
                            <ul style="margin:0; padding-left:1.2rem; font-size:0.9rem;">
                                <li v-for="doc in data.annulledUnreported.documents" :key="doc.saleId">
                                    <router-link :to="{name: 'saleForm', query: {id: doc.saleId}}">{{ doc.sequence }}</router-link>
                                    · {{ formatDate(doc.date) }} · IVA ${{ money(doc.iva) }}
                                    <small style="color:#999;">({{ annulmentLabel(doc.annulmentStatus) }})</small>
                                    <small v-if="doc.annulmentExpired" style="color:#b52b27;">
                                        · plazo vencido: el SRI ya no admite anularla, se declara tal cual
                                    </small>
                                </li>
                            </ul>
                        </template>
                        <template v-if="notesAnnulledUnreportedCount > 0">
                            <p style="font-size: 0.9rem; color: #555; max-width: 640px;"
                               :style="annulledUnreportedCount > 0 ? 'margin-top: 1rem;' : ''">
                                Anulaste estas <b>notas de crédito</b> en Contablus, pero el SRI no confirmó la
                                anulación: para el SRI siguen vigentes y <b>se restan del IVA generado</b>
                                (${{ money(data.creditNotesAnnulledUnreported.iva) }}). Si la anulación es real,
                                repórtala desde la nota; el cliente tiene 5 días hábiles para aceptarla.
                            </p>
                            <ul style="margin:0; padding-left:1.2rem; font-size:0.9rem;">
                                <li v-for="doc in data.creditNotesAnnulledUnreported.documents" :key="doc.creditNoteId">
                                    <router-link :to="{name: 'creditNoteForm', query: {id: doc.creditNoteId}}">N/C {{ doc.sequence }}</router-link>
                                    · {{ formatDate(doc.date) }} · IVA ${{ money(doc.iva) }}
                                    <small style="color:#999;">({{ annulmentLabel(doc.annulmentStatus) }})</small>
                                    <small v-if="doc.annulmentExpired" style="color:#b52b27;">
                                        · plazo vencido: el SRI ya no admite anularla, se resta tal cual
                                    </small>
                                </li>
                            </ul>
                        </template>
                    </div>

                    <!-- Lo que el mayor registra como IVA por pagar pero ninguna casilla
                         del 104 sustenta. Se muestra siempre: cero tambien es informacion. -->
                    <div class="card mt-3"
                         :style="hasUnreported || notesWithoutReceiptCount > 0
                                     ? 'background:#fff8e1; border-left:3px solid #f0ad4e;'
                                     : 'background:#fafafa;'">
                        <h6 style="margin-top:0;">
                            <i class="pi pi-file-o mr-2"></i>IVA sin respaldo en el SRI
                            <b style="float:right;">${{ money(data.ivaWithoutReceipt) }}</b>
                        </h6>
                        <p style="font-size: 0.9rem; color: #555; max-width: 640px;">
                            Tu contabilidad registró este IVA porque la venta ocurrió y se cobró, pero
                            <b>ninguna casilla del 104 lo sustenta</b>: el SRI no recibió el comprobante.
                            Es la diferencia entre lo que tu mayor dice que debes y lo que vas a declarar.
                        </p>
                        <div style="display:flex; gap:2rem; flex-wrap:wrap;">
                            <div>
                                <small style="color:#777;">Autorizadas por el SRI</small><br/>
                                <span>${{ money(data.salesByReceipt.authorized.iva) }}</span>
                                <small style="color:#999;"> · {{ data.salesByReceipt.authorized.count }} venta(s)</small>
                                <small v-if="annulledUnreportedCount > 0" style="display:block; color:#b52b27;">
                                    incluye {{ annulledUnreportedCount }} anulada(s) sin confirmar por el SRI
                                </small>
                            </div>
                            <div>
                                <small style="color:#777;">Emitidas sin autorizar</small><br/>
                                <span>${{ money(data.salesByReceipt.unreported.iva) }}</span>
                                <small style="color:#999;"> · {{ data.salesByReceipt.unreported.count }} venta(s)</small>
                            </div>
                            <div>
                                <small style="color:#777;">Archivadas, sin comprobante</small><br/>
                                <span>${{ money(data.salesByReceipt.archived.iva) }}</span>
                                <small style="color:#999;"> · {{ data.salesByReceipt.archived.count }} venta(s)</small>
                            </div>
                        </div>
                        <small v-if="data.salesByReceipt.unreported.count > 0"
                               style="display:block; margin-top:0.75rem; color:#8a5e12;">
                            Las emitidas sin autorizar pueden ser un envío que se quedó a medias: revísalas
                            antes de declarar, porque esas sí se arreglan reintentando.
                        </small>
                        <!-- Al revés que las ventas: IVA que el mayor ya restó y el 104 todavía no.
                             Es raro (la N/C casi siempre termina autorizada), por eso solo aparece
                             cuando hay alguna. -->
                        <div v-if="notesWithoutReceiptCount > 0" style="margin-top:1rem;">
                            <small style="color:#777;">Notas de crédito sin autorizar</small><br/>
                            <span>−${{ money(data.creditNotesWithoutReceipt.iva) }}</span>
                            <small style="color:#999;"> · {{ notesWithoutReceiptCount }} nota(s)</small>
                            <small style="display:block; color:#8a5e12; max-width: 640px;">
                                Tu contabilidad ya restó este IVA, pero el 104 solo admite restar notas autorizadas
                                por el SRI: <b>no están restadas arriba</b>. Ábrelas y pulsa «Generar», o espera la
                                autorización.
                            </small>
                            <ul style="margin:0.25rem 0 0; padding-left:1.2rem; font-size:0.9rem;">
                                <li v-for="doc in data.creditNotesWithoutReceipt.documents" :key="doc.creditNoteId">
                                    <router-link :to="{name: 'creditNoteForm', query: {id: doc.creditNoteId}}">{{ doc.document }}</router-link>
                                    · {{ formatDate(doc.date) }} · IVA ${{ money(doc.iva) }}
                                    <small style="color:#999;">({{ doc.state === 'archived' ? 'sin comprobante' : 'emitida, sin autorizar' }})</small>
                                </li>
                            </ul>
                        </div>
                    </div>

                    <!-- El valor de mirarlo desde Contabilidad: declaración vs mayor -->
                    <div class="card mt-3" style="background: #fafafa;">
                        <h6 style="margin-top:0;">
                            <i class="pi pi-check-circle mr-2"></i>Contraste con tu contabilidad
                        </h6>
                        <p style="font-size: 0.9rem; color: #555; max-width: 640px;">
                            Compara lo que dicen tus comprobantes contra lo que registró tu contabilidad en el
                            mismo período. Las anulaciones y las notas de crédito sin autorizar explican parte de
                            la diferencia y se listan como partidas; lo que quede sin explicar son documentos sin
                            asiento o asientos fuera de fecha — revísalo antes de declarar.
                        </p>
                        <div style="display:flex; gap:2rem; flex-wrap:wrap;">
                            <div v-for="side in contrast" :key="side.title" style="flex:1; min-width:260px;">
                                <small style="color:#777;">{{ side.title }}</small><br/>
                                <span>Comprobantes ${{ money(side.documents) }}</span><br/>
                                <!-- Partidas conciliatorias: comprobantes + partidas = lo que debería decir el mayor -->
                                <template v-if="side.items.length">
                                    <div v-for="item in side.items" :key="item.kind + item.docId"
                                         style="font-size:0.85rem; color:#555; padding-left:0.5rem;">
                                        {{ signed(item.amount) }} · {{ reconcilingLabel(item) }}
                                    </div>
                                    <span>Esperado en el mayor ${{ money(side.expected) }}</span><br/>
                                </template>
                                <span>Contabilidad ${{ money(side.ledger) }}</span>
                                <Tag class="ml-2" :severity="side.match ? 'success' : 'danger'"
                                     :value="side.match ? 'Coincide' : 'No coincide'"/>
                                <small v-if="!side.match" style="display:block; color:#b52b27;">
                                    Diferencia sin explicar ${{ money(side.unexplained) }}
                                </small>
                            </div>
                        </div>
                    </div>

                    <small style="color:#777; display:block; margin-top:0.75rem;">
                        Período {{ data.period.start }} a {{ data.period.end }} ·
                        {{ data.sales.count }} venta(s) · {{ data.creditNotes.count }} nota(s) de crédito ·
                        {{ data.purchases.count }} compra(s)
                        <span v-if="data.mode !== 'LIVE'"> · modo validación</span>
                    </small>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import download from 'downloadjs';
import httpRequest from '../../../service/httpRequest';

export default {
    data() {
        const now = new Date();
        return {
            loading: false,
            downloading: false,
            data: null,
            year: now.getFullYear(),
            month: now.getMonth() + 1,
            months: [
                {label: 'Enero', value: 1}, {label: 'Febrero', value: 2}, {label: 'Marzo', value: 3},
                {label: 'Abril', value: 4}, {label: 'Mayo', value: 5}, {label: 'Junio', value: 6},
                {label: 'Julio', value: 7}, {label: 'Agosto', value: 8}, {label: 'Septiembre', value: 9},
                {label: 'Octubre', value: 10}, {label: 'Noviembre', value: 11}, {label: 'Diciembre', value: 12},
            ],
        }
    },
    computed: {
        years() {
            const current = new Date().getFullYear();
            return [current, current - 1, current - 2, current - 3];
        },
        hasUnreported() {
            return this.data && Number(this.data.ivaWithoutReceipt || 0) > 0;
        },
        annulledUnreportedCount() {
            return (this.data && this.data.annulledUnreported) ? this.data.annulledUnreported.count : 0;
        },
        notesAnnulledUnreportedCount() {
            const notes = this.data && this.data.creditNotesAnnulledUnreported;
            return notes ? notes.count : 0;
        },
        notesWithoutReceiptCount() {
            const notes = this.data && this.data.creditNotesWithoutReceipt;
            return notes ? notes.count : 0;
        },
        // Tarifas donde las N/C superan las ventas, como las nombra la tabla.
        carryLabels() {
            const rates = (this.data && this.data.creditNotesToCarry) || [];
            return rates.map(rate => rate === 'otras' ? 'otras tarifas' : `la tarifa ${rate}%`).join(' y ');
        },
        contrast() {
            if (!this.data) return [];
            const l = this.data.ledger;
            return [
                // Contra el NETO: la N/C ya bajó el IVA en ventas del mayor (R13).
                {title: 'IVA en ventas', documents: this.data.netSales.iva, items: l.salesReconciling || [],
                 expected: l.expectedIvaSales, ledger: l.ivaSales, match: l.salesMatch,
                 unexplained: l.salesUnexplained},
                {title: 'IVA en compras', documents: this.data.purchases.iva, items: l.purchasesReconciling || [],
                 expected: l.expectedIvaPurchases, ledger: l.ivaPurchases, match: l.purchasesMatch,
                 unexplained: l.purchasesUnexplained},
            ];
        },
        rows() {
            if (!this.data) return [];
            const s = this.data.sales, n = this.data.creditNotes, net = this.data.netSales;
            const p = this.data.purchases;
            const row = (concept, key, strong) => ({
                concept: concept,
                sales: this.money(s[key]),
                notes: this.money(n[key]),
                net: this.money(net[key]),
                purchases: this.money(p[key]),
                strong: !!strong,
            });
            return [
                row('Base 0%', '0'),
                row('Base 5%', '5'),
                row('Base 15%', '15'),
                // Sin esta fila un comprobante histórico al 12% desaparecería del cuadre.
                row('Base otras tarifas', 'otras'),
                row('ICE', 'ice'),
                row('Total base imponible', 'base', true),
                row('IVA', 'iva', true),
            ];
        },
    },
    async mounted() {
        await this.load();
    },
    methods: {
        money(value) {
            return parseFloat(value || 0).toFixed(2);
        },
        signed(value) {
            const n = parseFloat(value || 0);
            return (n < 0 ? '− $' : '+ $') + Math.abs(n).toFixed(2);
        },
        formatDate(iso) {
            return iso ? iso.split('-').reverse().join('/') : '';
        },
        annulmentLabel(status) {
            return {
                unreported: 'anulada sin reportar',
                sending: 'enviándose al SRI',
                requested: 'solicitud en curso',
                dismissed: 'solicitud rechazada o vencida',
            }[status] || status;
        },
        // Por qué el mayor y los comprobantes difieren por este documento.
        reconcilingLabel(item) {
            switch (item.kind) {
                case 'annulledUnreported':
                    return item.docType === 'credit_note'
                        ? `${item.document} anulada sin confirmar por el SRI: se resta, pero el mayor ya la revirtió`
                        : `${item.document} anulada sin confirmar por el SRI: se declara, pero el mayor ya la revirtió`;
                case 'annulledLater':
                    return `${item.document} anulada el ${this.formatDate(item.reversedOn)}: el mayor la revierte ese día`;
                case 'otherPeriodReversal':
                    return `Reversa de ${item.document}, emitida el ${this.formatDate(item.emittedOn)}`;
                case 'creditNoteWithoutReceipt':
                    return `${item.document} sin autorizar: el mayor ya la restó, el 104 todavía no`;
                case 'gatewayCommissionIva':
                    // La liquidación de la pasarela pone este IVA en el crédito tributario sin
                    // una factura de compra detrás (H3).
                    return 'IVA de comisiones de pasarela: sin factura de compra en el período';
                default:
                    return item.document;
            }
        },
        // El Excel del mismo período que muestra la pantalla. Comparte la regla con el
        // módulo (exceldownloader/utils.py::taxSummaryTotals), así que da el mismo IVA.
        async downloadExcel() {
            const period = this.data.period;
            this.downloading = true;
            const file = await httpRequest.downloadTaxSummary(period.start, period.end);
            this.downloading = false;
            if (!(file instanceof Blob) || (file.type && file.type.indexOf('json') !== -1)) {
                let detail = 'No se pudo generar el Excel, inténtalo nuevamente';
                try { detail = JSON.parse(await file.text()).error || detail; } catch (e) { /* sin detalle */ }
                this.$toast.add({severity: 'error', summary: 'Descarga no disponible', detail: detail, life: 4000});
                return;
            }
            download(file, `resumen-iva-${period.start.slice(0, 7)}.xlsx`, 'application/vnd.ms-excel');
        },
        async load() {
            this.loading = true;
            const response = await httpRequest.getAccountingTaxSummary(this.year, this.month);
            this.loading = false;
            if (response.status === 200) {
                this.data = response.data;
            } else {
                this.$toast.add({severity: 'error', summary: 'Error',
                                 detail: response.error || 'No se pudo calcular el resumen', life: 4000});
            }
        },
    },
}
</script>
